import { ReactNode } from "react";
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

export interface DrawerNavMenuItemProps {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  path: string;
  collapsed?: boolean;
  // exact = current route must equal `path` exactly. Default is a
  // prefix match so /exports stays active for /exports/wf-1.
  exact?: boolean;
  // disabled = visible but muted + non-navigable (e.g. an admin-only
  // section shown to non-admins so they know it exists).
  disabled?: boolean;
}

// Flat nav item. Mirrors the Insight template's `DrawerNavMenuItem`:
// 48px row, icon-then-label, primary-color highlight when active,
// tooltip on the right when the drawer is collapsed.

export function DrawerNavMenuItem({
  icon,
  label,
  tooltip,
  path,
  collapsed = false,
  exact = false,
  disabled = false,
}: DrawerNavMenuItemProps) {
  const location = useLocation();
  const isActive =
    !disabled && (exact ? location.pathname === path : location.pathname.startsWith(path));

  const buttonSx = { borderRadius: 0.5, padding: 1, minWidth: 48 };
  const inner = (
    <>
      <ListItemIcon
        sx={{
          minWidth: 24,
          marginRight: 1.5,
          justifyContent: "flex-start",
          color: isActive ? "primary.main" : "text.secondary",
        }}
      >
        {icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          sx={{
            "& span": {
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            },
          }}
        />
      )}
    </>
  );

  // Disabled items render as a plain (non-Link) disabled button so they
  // can't navigate. A disabled MUI button swallows pointer events, so the
  // Tooltip is anchored on a wrapping span to still fire on hover.
  const button = disabled ? (
    <ListItemButton disabled disableGutters sx={buttonSx}>
      {inner}
    </ListItemButton>
  ) : (
    <ListItemButton component={Link} to={path} selected={isActive} disableGutters sx={buttonSx}>
      {inner}
    </ListItemButton>
  );

  const tipTitle = disabled ? (tooltip ?? "Admin only") : collapsed ? (tooltip ?? label) : "";

  return (
    <ListItem disablePadding disableGutters sx={{ height: 48 }}>
      <Tooltip title={tipTitle} placement="right">
        {disabled ? <span style={{ width: "100%", display: "block" }}>{button}</span> : button}
      </Tooltip>
    </ListItem>
  );
}
